import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLoader } from '../../../../context/LoaderProvider.jsx';

const getDefaultClientDetailsRows = () => [
    { key: "Owner Name", value: "", isUpload: false },
    { key: "Business Plan", value: "", isUpload: false },
    { key: "LOGO", value: null, isUpload: false },
    { key: "Company Name", value: "", isUpload: false },
    { key: "Register Documents", value: "", isUpload: true },
    { key: "Phone No", value: "", isUpload: false },
    { key: "Email ID", value: "", isUpload: false },
    {
        key: "Bank Details",
        value: {
            holderName: "",
            branchName: "",
            accountNumber: "",
            ifscCode: "",
        },
        isUpload: false,
    },
    { key: "Passbook", value: "", isUpload: true },
    { key: "AADHAR CARD NO", value: "", isUpload: false },
    { key: "PAN CARD NO", value: "", isUpload: false },
    { key: "Website", value: "", isUpload: false },
    { key: "GST No.", value: "", isUpload: false },
];

const STATUS_FIELD_MAP = {
    "Owner Name": "ownerNameStatus",
    "Business Plan": "businessPlanStatus",
    "Company Name": "registeredNameStatus",
    "Register Documents": "registeredDocumentsStatus",
    "Phone No": "mobileNumberStatus",
    "Email ID": "emailStatus",
    "AADHAR CARD NO": "aadharNumberStatus",
    "PAN CARD NO": "panCardStatus",
    "Website": "websiteNameStatus",
    "GST No.": "gstNumberStatus",
    "Trade License": "tradeLicenseStatus",
    "FSSI License": "fssaiLicenseStatus",
    "Labour License": "labourLicenseStatus",
    "Bank Details": "bankDetailsStatus",
    "Passbook": "passbookStatus",
    "Address": "addressStatus",
    "LOGO": "logoStatus",
    "Order Type": "orderTypeStatus",
    "Business Vertical": "businessVerticalStatus",
};

// Order Type options
const orderTypeOptions = [
    "DINE_IN",
    "TAKEAWAY",
    "DELIVERY",
    "TABLE_DINE_IN"
];

// Business Plan options
const businessPlanOptions = [
    "HOTEL",
    "RESTAURANT",
    "CAFE",
    "CLOUD_KITCHEN",
    "FOOD_COURT",
    "STREET_FOOD",
    "BAKERY"
];

// Safe date conversion utility
const safeDateToISOString = (dateString) => {
    if (!dateString || dateString === "N/A" || dateString === "Invalid Date") return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
};

// Safe date display utility
const safeDateDisplay = (dateString) => {
    if (!dateString || dateString === "N/A" || dateString === "Invalid Date") return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString();
};

// Add this function to check if a field has valid data
const hasValidData = (row, vendorDetails) => {
    if (!row || !vendorDetails) return false;

    const field = row.key;
    const value = row.value;

    // Check if the value exists and is not empty/"N/A"
    if (typeof value === 'string') {
        return value && value !== "N/A" && value.trim() !== "";
    }

    if (typeof value === 'object') {
        // For objects, check if any property has valid data
        return Object.values(value).some(val =>
            val && val !== "N/A" && val.toString().trim() !== ""
        );
    }

    return false;
};


const isFieldVerified = (rowKey, rowValue, vendorDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !vendorDetails) return false;

    
    if (vendorDetails[statusField] === true) return true;

    
    if ((vendorDetails[statusField] === null || vendorDetails[statusField] === undefined) &&
        hasValidData({ key: rowKey, value: rowValue }, vendorDetails)) {
        return true;
    }

    return false;
};


const isFieldRejected = (rowKey, vendorDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !vendorDetails) return false;

    
    return vendorDetails[statusField] === false;
};

/* ---------------- component ---------------- */
const FoodPaidClents = () => {
    /* -------- list‑screen state -------- */
    const [rolesData, setRolesData] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [verticalFilter, setVerticalFilter] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const { showLoader, hideLoader } = useLoader();
    /* -------- detail‑screen state -------- */
    const [selectedClient, setSelectedClient] = useState(null);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    const [rows, setRows] = useState(getDefaultClientDetailsRows());
    const [verifiedRows, setVerifiedRows] = useState([]);
    const [rejectedRows, setRejectedRows] = useState([]);
    const [isApproved, setIsApproved] = useState(false);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

    const fileInputRef = useRef(null);
    const [vendorData, setVendorData] = useState({});
    const [rowId, setRowId] = useState(null);

    const getCurrentVendorId = () => {
        return (
            selectedClient?.vendorId ||
            selectedClient?.vendorEnquiry?.vendorId ||
            vendorDetails?.vendorId ||
            null
        );
    };

    const vendorId = getCurrentVendorId();

    
    const handleImageError = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => new Set([...prev, errorKey]));
    };

    const handleImageLoad = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(errorKey);
            return newSet;
        });
    };

    const isImageFailed = (fieldName, index) => {
        return imageLoadErrors.has(`${fieldName}-${index}`);
    };

    const fetchVendorDetails = async (vendorId) => {
        if (!vendorId) return;
        setLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            //console.log(`🔍 FETCHING VENDOR DETAILS FOR ID: ${vendorId}`);

            const res = await fetch(
                `https://backend.maamaas.com/food/api/vendors/${vendorId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();

            //console.log("✅ API RESPONSE RECEIVED");
            setVendorDetails(data);

        } catch (err) {
            console.error("❌ get-vendor-details error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRow = async (rowIndex) => {
        const token = localStorage.getItem("authToken") || localStorage.getItem("token");

        if (!token) {
            alert("No authentication token found");
            return;
        }

        try {
            const vendorId = selectedClient?.vendorId;
            if (!vendorId) {
                console.error("Vendor ID not found");
                return;
            }

            const row = rows[rowIndex];
            //console.log("Editing row:", row);

            // Create FormData
            const formData = new FormData();

            // 1. Append vendorData as JSON string (your backend expects this)
            const vendorData = {};

            // Map row keys to actual backend field names
            const fieldMapping = {
                "Owner Name": "ownerName",
                "Phone No": "mobileNumber",
                "Website": "websiteName",
                "Email ID": "email",
                "Company Name": "registeredName",
                "AADHAR CARD NO": "aadharNumber",
                "PAN CARD NO": "panCardNumber",
                "GST No.": "gstNumber",
                "Order Type": "orderTypes",
                "Business Plan": "businessPlan",
                "Trade License Number": "tradeLicenseNumber",
                "FSSAI License Number": "fssaiLicenseNumber",
                "Labour License Number": "labourLicenseNumber",
            };

            // Add the specific field being edited
            const backendFieldName = fieldMapping[row.key];
            if (backendFieldName && row.value) {
                if (row.key === "Order Type") {
                    // Handle array for order types
                    vendorData[backendFieldName] = Array.isArray(row.value) ? row.value : [row.value];
                } else if (row.value !== "N/A") {
                    vendorData[backendFieldName] = row.value;
                }
            }

            
            if (row.key === "Address" && typeof row.value === "object") {
                vendorData.doorNumber = row.value.doorNo !== "N/A" ? row.value.doorNo : "";
                vendorData.addressLine = row.value.street !== "N/A" ? row.value.street : "";
                vendorData.city = row.value.city !== "N/A" ? row.value.city : "";
                vendorData.state = row.value.state !== "N/A" ? row.value.state : "";
                vendorData.pincode = row.value.pincode !== "N/A" ? row.value.pincode : "";
                vendorData.landMark = row.value.landmark !== "N/A" ? row.value.landmark : "";
            }

            if (row.key === "Bank Details" && typeof row.value === "object") {
                vendorData.holderName = row.value.holderName !== "N/A" ? row.value.holderName : "";
                vendorData.branchName = row.value.branchName !== "N/A" ? row.value.branchName : "";
                vendorData.accountNumber = row.value.accountNumber !== "N/A" ? row.value.accountNumber : "";
                vendorData.ifscCode = row.value.ifscCode !== "N/A" ? row.value.ifscCode : "";
            }

            if (row.key.includes("License") && typeof row.value === "object") {
                if (row.key === "Trade License") {
                    vendorData.tradeLicenseStartDate = row.value.startDate !== "N/A" ? row.value.startDate : "";
                    vendorData.tradeLicenseEndDate = row.value.endDate !== "N/A" ? row.value.endDate : "";
                }
                if (row.key === "FSSI License") {
                    vendorData.fssaiStartDate = row.value.startDate !== "N/A" ? row.value.startDate : "";
                    vendorData.fssaiEndDate = row.value.endDate !== "N/A" ? row.value.endDate : "";
                }
                if (row.key === "Labour License") {
                    vendorData.labourStartDate = row.value.startDate !== "N/A" ? row.value.startDate : "";
                    vendorData.labourEndDate = row.value.endDate !== "N/A" ? row.value.endDate : "";
                }
            }

            
            formData.append("vendorData", new Blob([JSON.stringify(vendorData)], {
                type: "application/json"
            }));

            
            if (row.images && row.images.length > 0) {
                row.images.forEach((img, idx) => {
                    if (img.file) {
                        const imageFieldName = getImageFieldName(row.key, idx);
                        formData.append(imageFieldName, img.file);
                        
                    }
                });
            }

            const response = await axios.put(
                `https://backend.maamaas.com/food/api/vendors/edit/${vendorId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            
            alert("Data updated successfully!");

            setRows(prev =>
                prev.map((r, idx) => (idx === rowIndex ? { ...r, isEditing: false } : r))
            );

            fetchVendorDetails(vendorId);

        } catch (err) {
            console.error("Error updating vendor:", err);
            if (err.response?.status === 401) {
                alert("Authentication failed. Please check your token.");
            } else {
                alert("Error updating data: " + (err.response?.data?.message || err.message));
            }
        }
    };

    const getImageFieldName = (rowKey, imageIndex) => {
        const fieldMap = {
            "Register Documents": imageIndex === 0 ? "registeredDocumentsFront" : "registeredDocumentsBack",
            "AADHAR CARD NO": imageIndex === 0 ? "aadharPhotoFront" : "aadharPhotoBack",
            "PAN CARD NO": "panCard",
            "Trade License": "tradeLicense",
            "FSSI License": "fssaiLicense",
            "Labour License": "labourLicense",
            "Bank Details": "blankCheque",
            "Passbook": "passbook",
            "LOGO": "companyLogo",
        };

        return fieldMap[rowKey] || `image${imageIndex + 1}`;
    };

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        showLoader();
        fetch("https://backend.maamaas.com/subscription/api/admin/get-all", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((data) => {
                const filtered = data.filter(item =>
                    item.businessVerticals?.includes("FOOD_AND_BEVERAGES")
                );
                setRolesData(filtered);

                if (filtered.length > 0) {
                    const vendorId = filtered[0]?.vendorId || filtered[0]?.vendor?.id;
                    if (vendorId) fetchVendorDetails(vendorId);
                }
            })
            .catch((err) => console.error("Payment fetch error:", err)).finally(() => {
                hideLoader();
            })
    }, []);

    useEffect(() => {
        let data = rolesData;
        if (statusFilter) data = data.filter((i) => i.status === statusFilter);
        if (verticalFilter) data = data.filter((i) => i.businessVertical === verticalFilter);
        if (planFilter) data = data.filter((i) => i.businessPlan === planFilter);
        setFilteredData(data);
    }, [statusFilter, verticalFilter, planFilter, rolesData]);

    useEffect(() => {
        if (!selectedClient) {
            setRows(getDefaultClientDetailsRows());
            setVerifiedRows([]);
            setRejectedRows([]);
            setIsApproved(false);
            setImageLoadErrors(new Set());
            return;
        }

        const merged = { ...selectedClient, ...(vendorDetails || {}) };

        // REQUIRED Image mapping only
        const imageMap = {
            "Register Documents": [
                merged.registeredDocumentsFront,
                merged.registeredDocumentsBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "AADHAR CARD NO": [
                merged.aadharPhotoFront,
                merged.aadharPhotoBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "PAN CARD NO": [merged.panCard]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Trade License": [merged.tradeLicense]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "FSSI License": [merged.fssaiLicense]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Labour License": [merged.labourLicense]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Bank Details": [merged.blankCheque]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Passbook": [merged.passbook]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "LOGO": [merged.companyLogo]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
        };

        const newRows = [
            { key: "Owner Name", value: merged.ownerName || "N/A" },
            { key: "Company Name", value: merged.registeredName || "N/A" },
            { key: "LOGO", value: "Company Logo" },
            { key: "Business Plan", value: merged.businessPlan || "N/A" },
            { key: "Business Vertical", value: merged.businessVertical || "N/A" },

            {
                key: "Order Type",
                value: Array.isArray(merged.orderTypes)
                    ? merged.orderTypes
                    : typeof merged.orderTypes === "string" && merged.orderTypes !== "N/A"
                        ? merged.orderTypes.split(",").map(v => v.trim())
                        : [],
            },

            { key: "Website", value: merged.websiteName || "N/A" },
            { key: "Phone No", value: merged.mobileNumber || "N/A" },
            { key: "Email ID", value: merged.email || "N/A" },

            {
                key: "Address",
                value: {
                    doorNo: merged.doorNumber || "N/A",
                    street: merged.addressLine || "N/A",
                    city: merged.city || "N/A",
                    state: merged.state || "N/A",
                    pincode: merged.pincode || "N/A",
                    landmark: merged.landMark || "N/A",
                },
            },

            { key: "Register Documents", value: "Registered Documents" },
            { key: "AADHAR CARD NO", value: merged.aadharNumber || "N/A" },
            { key: "PAN CARD NO", value: merged.panCardNumber || "N/A" },

            // ⭐ Trade License Number added here
            { key: "Trade License Number", value: merged.tradeLicenseNumber || "N/A" },
            {
                key: "Trade License",
                value: {
                    startDate: merged.tradeLicenseStartDate || "N/A",
                    endDate: merged.tradeLicenseEndDate || "N/A",
                },
            },

            // ⭐ FSSAI License Number added here
            { key: "FSSAI License Number", value: merged.fssaiLicenseNumber || "N/A" },
            {
                key: "FSSI License",
                value: {
                    startDate: merged.fssaiStartDate || "N/A",
                    endDate: merged.fssaiEndDate || "N/A",
                },
            },

            // ⭐ Labour License Number added here
            { key: "Labour License Number", value: merged.labourLicenseNumber || "N/A" },
            {
                key: "Labour License",
                value: {
                    startDate: merged.labourStartDate || "N/A",
                    endDate: merged.labourEndDate || "N/A",
                },
            },

            { key: "GST No.", value: merged.gstNumber || "N/A" },

            {
                key: "Bank Details",
                value: {
                    holderName: merged.holderName || merged.accountHolderName || "N/A",
                    branchName: merged.branchName || merged.bankName || "N/A",
                    accountNumber: merged.accountNumber || merged.accountNo || "N/A",
                    ifscCode: merged.ifscCode || "N/A",
                },
            },

            { key: "Passbook", value: "Passbook Image" },
        ];


        const updatedRows = newRows.map((row) => ({
            ...row,
            images: imageMap[row.key] || [],
        }));

        setRows(updatedRows.map((r) => ({ ...r, isEditing: false })));
        setVerifiedRows([]);
        setRejectedRows([]);
        setIsApproved(false);
        setImageLoadErrors(new Set());
    }, [selectedClient, vendorDetails]);

    const handleViewClick = (client) => {
        //console.log("👁️ VIEW CLICKED FOR CLIENT:", client);
        setSelectedClient(client);
        const vendorId = client?.vendorEnquiry?.vendorId || client.vendorId || client.vendorID;
        ////console.log("🔍 FETCHING VENDOR DETAILS FOR ID:", vendorId);
        fetchVendorDetails(vendorId);
    };

    const handleReject = async (i) => {
        const normalisedKey = rows[i].key.trim().replace(/\.$/, "");
        const statusField = STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) {
            setRejectedRows(prev => (prev.includes(i) ? prev : [...prev, i]));
            return;
        }

        const vendorId = getCurrentVendorId();
        if (!vendorId) return alert("Vendor ID is missing – cannot update.");

        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing. Please log in.");

        const url = `https://backend.maamaas.com/food/api/vendors/edit/${vendorId}`;

        try {
            const formData = new FormData();
            const vendorData = {
                ...vendorDetails,
                [statusField]: false
            };

            formData.append(
                "vendorData",
                new Blob([JSON.stringify(vendorData)], { type: "application/json" })
            );

            if (vendorDetails?.images?.length) {
                vendorDetails.images.forEach((img, index) => {
                    if (img.file) formData.append(`image${index + 1}`, img.file);
                });
            }

            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const responseText = await res.text();
            //console.log("❌ Reject API Response Status:", res.status);

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            alert("Vendor status rejected successfully!");
        } catch (err) {
            console.error("❌ Reject error:", err);
            alert(`Failed to reject status: ${err.message}`);
        }
    };

    const handleVerifyy = async (i) => {
        const normalisedKey = rows[i].key.trim().replace(/\.$/, "");
        const statusField = STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) {
            console.warn("No status mapping for:", rows[i].key);
            return;
        }

        const vendorId = getCurrentVendorId();
        if (!vendorId) return alert("Vendor ID is missing");

        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing");

        const url = `https://backend.maamaas.com/food/api/vendors/edit/${vendorId}`;

        try {
            const formData = new FormData();
            const currentStatus = vendorDetails?.[statusField];
            const newStatus = currentStatus === null ? true : true;

            const vendorData = {
                [statusField]: newStatus
            };

            formData.append(
                "vendorData",
                new Blob([JSON.stringify(vendorData)], { type: "application/json" })
            );

            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const responseText = await res.text();
            //console.log("✅ Verify API Response Status:", res.status);

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${responseText}`);
            }

            await fetchVendorDetails(vendorId);
            alert("Vendor data updated successfully!");
        } catch (err) {
            console.error("❌ Verify error:", err);
            alert(`Failed to update status: ${err.message}`);
        }
    };

    const handleBackClick = () => setSelectedClient(null);

    const handleFileChange = (e, index) => {
        const fileName = e.target.files[0]?.name || "";
        const updated = [...rows];
        updated[index].value = fileName;
        setRows(updated);
    };

    // Clean Image Display Component with "-" for missing images
    const ImageDisplay = ({ images, fieldName }) => {
        if (!images || images.length === 0) {
            return (
                <div className="text-center">
                    <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                </div>
            );
        }

        return (
            <div className="d-flex justify-content-center gap-2 flex-wrap">
                {images.map((img, idx) => {
                    const imageUrl = typeof img === 'string' ? img : img.url;
                    const hasFailed = isImageFailed(fieldName, idx);

                    if (hasFailed || !imageUrl) {
                        return (
                            <div key={idx} className="text-center">
                                <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className="text-center position-relative">
                            <img
                                src={imageUrl}
                                alt={`${fieldName}-${idx}`}
                                style={{
                                    width: fieldName === "LOGO" ? "80px" : "60px",
                                    height: fieldName === "LOGO" ? "80px" : "60px",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    border: "1px solid #dee2e6",
                                }}
                                onError={() => handleImageError(imageUrl, fieldName, idx)}
                                onLoad={() => handleImageLoad(imageUrl, fieldName, idx)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12" style={{ marginTop: "10px" }}>
                    <div className="client" style={{ overflowY: "auto", height: "500px" }}>
                        {!selectedClient ? (
                            <>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div></div>
                                    <div className="pe-3"></div>
                                </div>

                                <div className="sed pe-3">
                                    <table style={tableStyle} className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>S.NO</th>
                                                <th style={thStyle}>Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Mobile</th>
                                                <th style={thStyle}>City</th>
                                                <th style={thStyle}>Vertical</th>
                                                <th style={thStyle}>Plan</th>
                                                <th style={thStyle}>Vendor Id</th>
                                                <th style={thStyle}>Payment Id</th>
                                                <th style={thStyle}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.length ? (
                                                filteredData.map((c, idx) => (
                                                    <tr key={c.vendorId || idx}>
                                                        <td style={thTdStyle}>{idx + 1}</td>
                                                        <td style={thTdStyle}>{c?.vendorName || "—"}</td>
                                                        <td style={thTdStyle}>{c?.email || "—"}</td>
                                                        <td style={thTdStyle}>{c?.mobileNumber || "—"}</td>
                                                        <td style={thTdStyle}>{c?.city || "—"}</td>
                                                        <td style={thTdStyle}>{c?.businessVertical || "—"}</td>
                                                        <td style={thTdStyle}>{c?.planType || "—"}</td>
                                                        <td style={thTdStyle}>{c?.vendorId || "—"}</td>
                                                        <td style={thTdStyle}>{c?.id || "—"}</td>
                                                        <td style={thTdStyle}>
                                                            <button
                                                                style={{ ...buttonStyle, color: "#b15cd6", fontWeight: "bold" }}
                                                                onClick={() => handleViewClick(c)}
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td style={thTdStyle} colSpan={13}>
                                                        No data available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="mt-4 rounded-3 shadow-sm">
                                {/* <div className="d-flex align-items-center mb-4">
                                    <button
                                        onClick={handleBackClick}
                                        className="btn fw-bold px-4 py-2"
                                        style={{ backgroundColor: "#b15cd6", color: "#fff" }}
                                    >
                                        Back to List
                                    </button>
                                </div> */}

                                {loading && (
                                    <div className="text-center my-4">
                                        <span className="spinner-border text-primary" />
                                    </div>
                                )}

                                {!loading && (
                                    <div className="register mt-4">
                                        <div className="registertable bo2">
                                            <table className="table table-bordered table-striped align-middle mt-4 w-100">
                                                <thead>
                                                    <tr>
                                                        <th style={detailThStyle}>Field</th>
                                                        <th style={detailThStyle}>Value</th>
                                                        <th style={detailThStyle}>Image</th>
                                                        <th style={detailThStyle}>Status</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {rows.map((r, i) => {
                                                        const isEditing = r.isEditing || false;

                                                        const handleInputChange = (field, value) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            value:
                                                                                typeof row.value === "object"
                                                                                    ? { ...row.value, [field]: value }
                                                                                    : value,
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        // Handle Order Type selection (multiple)
                                                        const handleOrderTypeChange = (selectedValue) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) => {
                                                                    if (idx === i) {
                                                                        const currentValues = Array.isArray(row.value) ? row.value : [];
                                                                        const isSelected = currentValues.includes(selectedValue);
                                                                        
                                                                        if (isSelected) {
                                                                            // Remove if already selected
                                                                            return {
                                                                                ...row,
                                                                                value: currentValues.filter(v => v !== selectedValue)
                                                                            };
                                                                        } else {
                                                                            // Add if not selected
                                                                            return {
                                                                                ...row,
                                                                                value: [...currentValues, selectedValue]
                                                                            };
                                                                        }
                                                                    }
                                                                    return row;
                                                                })
                                                            );
                                                        };

                                                        // Handle Business Plan selection
                                                        const handleBusinessPlanChange = (selectedValue) => {
                                                            setRows((prevRows) =>
                                                                prevRows.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            value: selectedValue,
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        const handleImageChange = (e) => {
                                                            const files = Array.from(e.target.files);
                                                            const newImages = files.map((file) => ({
                                                                url: URL.createObjectURL(file),
                                                                file
                                                            }));

                                                            setRows(prev =>
                                                                prev.map((row, idx) =>
                                                                    idx === i
                                                                        ? {
                                                                            ...row,
                                                                            images: [...(row.images || []), ...newImages]
                                                                        }
                                                                        : row
                                                                )
                                                            );
                                                        };

                                                        return (
                                                            <tr key={r.key || i}>
                                                                <td className="fw-bold text-center align-middle">{r.key}</td>

                                                                <td className="text-start align-middle">
                                                                    {r.key === "Order Type" ? (
                                                                        isEditing ? (
                                                                            <div className="d-flex flex-column gap-2">
                                                                                {orderTypeOptions.map((option) => {
                                                                                    const currentValues = Array.isArray(r.value) ? r.value : [];
                                                                                    const isSelected = currentValues.includes(option);
                                                                                    
                                                                                    return (
                                                                                        <div key={option} className="form-check">
                                                                                            <input
                                                                                                className="form-check-input"
                                                                                                type="checkbox"
                                                                                                id={`order-type-${option}-${i}`}
                                                                                                checked={isSelected}
                                                                                                onChange={() => handleOrderTypeChange(option)}
                                                                                            />
                                                                                            <label
                                                                                                className="form-check-label"
                                                                                                htmlFor={`order-type-${option}-${i}`}
                                                                                            >
                                                                                                {option}
                                                                                            </label>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ) : (
                                                                            Array.isArray(r.value) && r.value.length > 0 
                                                                                ? r.value.join(", ") 
                                                                                : "N/A"
                                                                        )
                                                                    ) : r.key === "Business Plan" ? (
                                                                        isEditing ? (
                                                                            <select
                                                                                value={r.value || ""}
                                                                                onChange={(e) => handleBusinessPlanChange(e.target.value)}
                                                                                className="form-select w-75"
                                                                            >
                                                                                <option value="">Select Business Plan</option>
                                                                                {businessPlanOptions.map((option) => (
                                                                                    <option key={option} value={option}>
                                                                                        {option}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            r.value || "N/A"
                                                                        )
                                                                    ) : r.key === "Bank Details" ? (
                                                                        ["holderName", "branchName", "accountNumber", "ifscCode"].map((field) => (
                                                                            <div key={field} className="mb-1">
                                                                                <strong>{field.replace(/([A-Z])/g, " $1")}:</strong>{" "}
                                                                                {isEditing ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={r.value?.[field] || ""}
                                                                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    r.value?.[field] || "N/A"
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    ) : r.key === "Address" ? (
                                                                        ["doorNo", "street", "city", "state", "pincode", "landmark"].map((field) => (
                                                                            <div key={field} className="mb-1">
                                                                                <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{" "}
                                                                                {isEditing ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={r.value?.[field] || ""}
                                                                                        onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    r.value?.[field] || "N/A"
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    ) : r.key === "Trade License" ||
                                                                        r.key === "FSSI License" ||
                                                                        r.key === "Labour License" ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong>Start Date:</strong>{" "}
                                                                                {isEditing ? (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={safeDateToISOString(r.value?.startDate)}
                                                                                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    safeDateDisplay(r.value?.startDate)
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <strong>End Date:</strong>{" "}
                                                                                {isEditing ? (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={safeDateToISOString(r.value?.endDate)}
                                                                                        onChange={(e) => handleInputChange("endDate", e.target.value)}
                                                                                        className="form-control d-inline-block w-75 ms-2"
                                                                                    />
                                                                                ) : (
                                                                                    safeDateDisplay(r.value?.endDate)
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    ) : r.key === "Trade License Number" ||
                                                                        r.key === "Labour License Number" ||
                                                                        r.key === "FSSAI License Number" ? (
                                                                        isEditing ? (
                                                                            <input
                                                                                type="text"
                                                                                value={r.value || ""}
                                                                                onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                className="form-control d-inline-block w-75"
                                                                            />
                                                                        ) : (
                                                                            r.value || "N/A"
                                                                        )
                                                                    ) : r.key === "Register Documents" ? (
                                                                        "Registered Documents"
                                                                    ) : r.key === "Passbook" ? (
                                                                        "Passbook Image"
                                                                    ) : r.key === "LOGO" ? (
                                                                        "Company Logo"
                                                                    ) : (
                                                                        isEditing ? (
                                                                            <input
                                                                                type="text"
                                                                                value={r.value || ""}
                                                                                onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                className="form-control d-inline-block w-75"
                                                                            />
                                                                        ) : (
                                                                            r.value || "N/A"
                                                                        )
                                                                    )}
                                                                </td>


                                                                <td className="text-center align-middle">
                                                                    {isEditing ? (
                                                                        <>
                                                                            <input
                                                                                type="file"
                                                                                multiple={r.key === "Register Documents" || r.key === "AADHAR CARD NO"}
                                                                                accept="image/*"
                                                                                onChange={handleImageChange}
                                                                                className="form-control mb-2"
                                                                            />
                                                                            <ImageDisplay images={r.images} fieldName={r.key} />
                                                                        </>
                                                                    ) : (
                                                                        <ImageDisplay images={r.images} fieldName={r.key} />
                                                                    )}
                                                                </td>

                                                                <td className="text-center align-middle">
                                                                    {isEditing ? (
                                                                        <>
                                                                            <button
                                                                                className="btn btn-sm btn-success me-2"
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() => handleSaveRow(i)}
                                                                            >
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-secondary"
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() =>
                                                                                    setRows((prev) =>
                                                                                        prev.map((row, idx) =>
                                                                                            idx === i ? { ...row, isEditing: false } : row
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <div style={{ display: "flex", gap: "10px" }}>
                                                                            <button
                                                                                className={`btn btn-sm ${isFieldVerified(r.key, r.value, vendorDetails)
                                                                                    ? "btn-success"
                                                                                    : "btn-warning"
                                                                                    }`}
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() => handleVerifyy(i)}
                                                                                disabled={
                                                                                    isFieldVerified(r.key, r.value, vendorDetails) ||
                                                                                    isFieldRejected(r.key, vendorDetails)
                                                                                }
                                                                            >
                                                                                {isFieldVerified(r.key, r.value, vendorDetails) ? "Verified" : "Verify"}
                                                                            </button>

                                                                            <button
                                                                                className={`btn btn-sm ${isFieldRejected(r.key, vendorDetails) ? "btn-danger" : "btn-secondary"
                                                                                    }`}
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() => handleReject(i)}
                                                                                disabled={
                                                                                    isFieldRejected(r.key, vendorDetails) ||
                                                                                    isFieldVerified(r.key, r.value, vendorDetails)
                                                                                }
                                                                            >
                                                                                {isFieldRejected(r.key, vendorDetails) ? "Rejected" : "Reject"}
                                                                            </button>

                                                                            <button
                                                                                className="btn btn-sm btn-primary"
                                                                                style={{ minWidth: 80 }}
                                                                                onClick={() =>
                                                                                    setRows((prev) =>
                                                                                        prev.map((row, idx) =>
                                                                                            idx === i ? { ...row, isEditing: true } : row
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 5 };
const thTdStyle = { border: "1px solid #ddd", textAlign: "center" };
const thStyle = { ...thTdStyle, backgroundColor: "#b15cd6", color: "#fff", fontWeight: "bold", maxWidth: "70px" };
const buttonStyle = { backgroundColor: "transparent", border: "none", cursor: "pointer" };

const detailThStyle = {
    ...thTdStyle,
    backgroundColor: "#b15cd6",
    color: "#fff",
    fontWeight: 600,
    padding: "0.75rem",
    verticalAlign: "middle",
    border: "1px solid #dee2e6",
    textAlign: "center"
};

export default FoodPaidClents;